import React, { useEffect, useRef } from 'react';
import { useCookies } from 'next-client-cookies';
import { useTranslations } from 'use-intl';
import { SuperCartList, formatMoney } from 'components/commercetools-ui/organisms/mini-cart';
import { getTotalItemsOfCarts } from 'components/commercetools-ui/organisms/cart/helpers';
import { CloseIcon } from 'components/icons';
import type { Cart } from 'shared/types/cart';
import { Money } from 'types/entity/product';
import { HeaderContentProps } from '../../types/gmsb-header';

export type MenuState = 'cart';

export interface SlideOutProps {
  carts?: Cart[];
  onClose?: () => void;
  contentConfig: HeaderContentProps;
}

const Slideout: React.FC<SlideOutProps> = ({ carts, onClose, contentConfig }) => {
  const translate = useTranslations();
  const cookies = useCookies();
  const locale = cookies.get('locale') as string;
  const { miniCartNoOfItemsText, minicartText } = contentConfig;

  const sumCartsToMoney = (carts: Cart[] = []): Money => {
    const baseline = carts.find((c) => c?.subTotalAmount?.currencyCode)?.subTotalAmount ?? {
      currencyCode: 'USD',
      fractionDigits: 2,
    };
    const totalCentAmount = carts.reduce((acc, cart) => acc + (cart?.subTotalAmount?.centAmount ?? 0), 0);

    return {
      currencyCode: baseline.currencyCode ?? 'USD',
      fractionDigits: baseline.fractionDigits ?? 2,
      centAmount: totalCentAmount,
    };
  };

  const formatCartsTotal = (carts: Cart[] = [], locale = 'en-US'): string =>
    formatMoney(sumCartsToMoney(carts), locale);

  const formatted = formatCartsTotal(carts, locale);
  const totalCartItems = getTotalItemsOfCarts(carts);
  const closeButtonRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      closeButtonRef.current?.focus();
    }, 0);

    return () => clearTimeout(timer);
  }, []);

  return (
    <>
      <div data-ffid="ctr-minicart-section" className="flex items-center justify-between px-36 py-28 pb-24">
        <h3 data-ffid="ctr-minicart-header" className="text-18 font-normal leading-normal text-[#333] md:text-18">
          <span data-ffid="lbl-minicart-cart-text">{`${minicartText} `}</span> {`(`}
          <span data-ffid="lbl-minicart-total-cart-items">{totalCartItems} </span>{' '}
          <span data-ffid="lbl-no-of-items-text">{miniCartNoOfItemsText}</span>
          {`)`} <span data-ffid="lbl-minicart-total-price">{formatted}</span>
        </h3>
        <div className="flex h-full items-center gap-24">
          <button
            onClick={onClose}
            ref={closeButtonRef}
            data-ffid="btn-minicart-close-button"
            className="cursor-pointer transition hover:opacity-80"
            data-testid="close-icon"
            aria-label={translate('common.close')}
          >
            <CloseIcon className="w-20 text-[#333]" />
          </button>
        </div>
      </div>
      <SuperCartList data={carts} contentConfig={contentConfig} />
    </>
  );
};

export default Slideout;
